import { useMemo } from "react";
import { useNavigate } from "react-router";

import type { AutomatedSurveyDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DotsHorizontalIcon } from "@/components/Icons/DotsHorizontalIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Pagination } from "@/components/Pagination/Pagination";
import { PillList } from "@/components/Pill/PillList";
import { Button } from "@/design-system/Button";
import { SectionCard } from "@/design-system/SectionCard";
import { createDashboardColumnHelper } from "@/design-system/Table/helpers";
import { TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface AutomatedSurveysListProps {
  automatedSurveys: AutomatedSurveyDto[];
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  totalPages: number;
}

export function AutomatedSurveysList({
  automatedSurveys,
  isLoading,
  page,
  onPageChange,
  totalPages,
}: AutomatedSurveysListProps): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<AutomatedSurveyDto>();

    return [
      helper.accessor("title", {
        header: t("page.automated-surveys.list.table.name"),
        cell: (cell) => cell.getValue(),
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("category", {
        header: t("page.automated-surveys.list.table.type"),
        cell: (cell) => {
          switch (cell.getValue()) {
            case "other":
              return <span className="whitespace-nowrap">{t("page.automated-surveys.types.other")}</span>;
            case "feelAtHome":
              return <span className="whitespace-nowrap">{t("page.automated-surveys.types.feeling-at-home")}</span>;
          }
        },
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("enabledProjects", {
        header: t("page.automated-surveys.list.table.projects"),
        cell: (cell) => (
          <PillList
            items={cell.getValue().map((project) => ({
              label: project.name,
            }))}
            maxShown={3}
          />
        ),
        minSize: TABLE_COLUMN_SIZES.TAGS.MIN,
        size: TABLE_COLUMN_SIZES.TAGS.DEFAULT,
      }),
      helper.accessor("questionGroups", {
        header: () => (
          <span className="whitespace-nowrap normal-case">
            {t("page.automated-surveys.list.table.n-of-questions.header")}
          </span>
        ),
        cell: (cell) => (
          <span className="whitespace-nowrap">
            {t("page.automated-surveys.list.table.n-of-questions.cell", { count: cell.getValue().length })}
          </span>
        ),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("isLocked", {
        header: t("page.automated-surveys.list.table.locked"),
        cell: (cell) => (
          <span className="whitespace-nowrap">
            {cell.getValue()
              ? t("page.automated-surveys.list.table.locked.yes")
              : t("page.automated-surveys.list.table.locked.no")}
          </span>
        ),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const id = cell.getValue();

          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              icon: <Edit05Icon />,
              value: "edit",
              callbackFn: () => void navigate(routes.automatedSurveys.edit({ id })),
            },
            {
              labels: {
                default: t("page.automated-surveys.list.table.actions.view-queue"),
              },
              icon: <DotsHorizontalIcon />,
              value: "view",
              callbackFn: () => void navigate(routes.automatedSurveys.details({ id })),
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [navigate, t]);

  const tableInstance = useResizableTable(TABLE_KEYS.AUTOMATED_SURVEYS, {
    columns,
    data: automatedSurveys,
    enableSorting: false,
  });

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.automated-surveys.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: t("component.breadcrumbs.automated-surveys"),
              to: routes.automatedSurveys.list(),
            },
          ]}
        />
      }
      actions={
        <Button type="link" href={routes.automatedSurveys.create()}>
          {t("page.automated-surveys.list.create-new")}
        </Button>
      }
    >
      {(automatedSurveys.length > 0 || isLoading) && (
        <Table
          table={tableInstance}
          isLoading={isLoading}
          getRowHref={(row) => routes.automatedSurveys.details({ id: row.original.id })}
        >
          <Pagination count={totalPages} onChange={onPageChange} currentIndex={page} />
        </Table>
      )}
      {!isLoading && !automatedSurveys.length && (
        <SectionCard>
          <p>{t("page.automated-surveys.list.no-data")}</p>
        </SectionCard>
      )}
    </DocumentPage>
  );
}
